import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Megaphone, Plus, Image as ImageIcon, ShieldCheck, MousePointerClick, Eye, Loader2, Sparkles } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import { BannerPreviewModal } from '@/components/dashboard/BannerPreviewModal';

const PAGES = ['buy', 'rent', 'projects', 'plots', 'commercial', 'agencies'];
const PLACEMENTS = [
  { value: 'right_sidebar', label: 'Right Sidebar' },
  { value: 'left_sidebar', label: 'Left Sidebar' },
  { value: 'homepage_banner', label: 'Homepage Banner' },
  { value: 'agencies_inline_mobile', label: 'Agencies (Mobile)' },
];

interface Subscription {
  id: string;
  package_id: string | null;
  status: string;
  expires_at: string;
  package?: { name: string; tier_label: string | null; allows_banner_ads: boolean; banner_ad_quota: number; } | null;
}

interface Campaign {
  id: string;
  title: string;
  image_url: string;
  placement: string;
  pages: string[];
  start_date: string;
  end_date: string;
  is_active: boolean;
  impressions: number;
  clicks: number;
}

export default function MyBanners() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [placement, setPlacement] = useState('right_sidebar');
  const [pages, setPages] = useState<string[]>(['buy', 'rent']);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(addDays(new Date(), 30), 'yyyy-MM-dd'));

  const { data: sub } = useQuery({
    queryKey: ['my-active-subscription', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('subscriptions' as any)
        .select('id, package_id, status, expires_at, advertising_packages!subscriptions_package_id_fkey(name, tier_label, allows_banner_ads, banner_ad_quota)')
        .eq('user_id', user!.id)
        .in('status', ['active', 'pending'])
        .order('expires_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const row: any = data;
      if (!row) return null;
      return { ...row, package: row.advertising_packages } as Subscription;
    },
    enabled: !!user,
  });

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['my-banner-campaigns', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('ad_campaigns' as any)
        .select('*')
        .eq('created_by', user!.id)
        .order('created_at', { ascending: false });
      return (data as unknown as Campaign[]) || [];
    },
    enabled: !!user,
  });

  const allowed = sub?.package?.allows_banner_ads;
  const quota = sub?.package?.banner_ad_quota || 0;
  const activeCount = (campaigns || []).filter((c) => c.is_active && new Date(c.end_date) >= new Date()).length;
  const remaining = Math.max(0, quota - activeCount);

  useEffect(() => {
    if (sub?.expires_at) setEndDate(format(new Date(sub.expires_at), 'yyyy-MM-dd'));
  }, [sub?.expires_at]);

  const togglePage = (p: string) => {
    setPages((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const handleUpload = async (file: File) => {
    if (!user) return;
    setUploading(true);
    const path = `${user.id}/${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
    const { error } = await supabase.storage.from('agency-logos').upload(path, file, { upsert: true });
    if (error) { setUploading(false); toast.error(error.message); return; }
    const { data } = supabase.storage.from('agency-logos').getPublicUrl(path);
    setImageUrl(data.publicUrl);
    setUploading(false);
  };

  const submit = async () => {
    if (!user) return;
    if (!title.trim() || !imageUrl) { toast.error('Title and image are required'); return; }
    if (pages.length === 0) { toast.error('Pick at least one page'); return; }
    setSubmitting(true);
    const { error } = await supabase.from('ad_campaigns' as any).insert({
      advertiser_name: (user.user_metadata?.full_name as string) || user.email || 'Advertiser',
      advertiser_contact: user.email || null,
      title: title.trim(),
      description: description.trim() || null,
      image_url: imageUrl,
      target_url: targetUrl.trim() || null,
      placement,
      pages,
      tier: 'standard',
      start_date: startDate,
      end_date: endDate,
      is_active: false,
      created_by: user.id,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Banner submitted! Our ad ops team will review and activate it.');
    setOpen(false);
    setTitle(''); setDescription(''); setImageUrl(''); setTargetUrl('');
    qc.invalidateQueries({ queryKey: ['my-banner-campaigns'] });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" /> My Banner Ads</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sub ? (
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                You need an active advertising package that includes banner ads. Visit{' '}
                <a className="underline text-primary" href="/advertise">/advertise</a> to choose a plan.
              </AlertDescription>
            </Alert>
          ) : !allowed ? (
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                Your current package <strong>{sub.package?.tier_label || sub.package?.name}</strong> doesn't include banner ads.
                Upgrade on <a className="underline text-primary" href="/advertise">/advertise</a>.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary">Plan: {sub.package?.tier_label || sub.package?.name}</Badge>
              <Badge variant="outline">Quota: {activeCount} / {quota} active</Badge>
              <Badge variant="outline">Plan ends {format(new Date(sub.expires_at), 'd MMM yyyy')}</Badge>
              <div className="ml-auto">
                <Button onClick={() => setOpen(true)} disabled={remaining === 0}>
                  <Plus className="h-4 w-4 mr-2" /> Create Banner ({remaining} left)
                </Button>
              </div>
            </div>
          )}

          {isLoading ? <Skeleton className="h-32 w-full" /> : (campaigns || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No banners yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(campaigns || []).map((c) => (
                <div key={c.id} className="border rounded-lg p-3 flex gap-3">
                  <img src={c.image_url} alt={c.title} className="h-20 w-32 object-cover rounded shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-sm truncate">{c.title}</h3>
                      <Badge variant={c.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {c.is_active ? 'Live' : 'Pending review'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{c.placement.replace(/_/g, ' ')} · {c.pages.join(', ')}</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(c.start_date), 'd MMM')} → {format(new Date(c.end_date), 'd MMM yyyy')}</p>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{c.impressions}</span>
                      <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{c.clicks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Banner Ad</DialogTitle>
            <DialogDescription>Submitted banners go through ad-ops review before going live.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Summer Apartments Promo" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <Label>Banner image *</Label>
              <div className="flex items-center gap-3">
                {imageUrl ? (
                  <img src={imageUrl} className="h-20 w-32 object-cover rounded border" alt="preview" />
                ) : (
                  <div className="h-20 w-32 rounded border border-dashed flex items-center justify-center text-muted-foreground">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                <Input type="file" accept="image/*" disabled={uploading}
                  onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
                {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Recommended: 600×600 (sidebar) or 1200×300 (homepage).</p>
            </div>
            <div>
              <Label>Click-through URL</Label>
              <Input type="url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Placement</Label>
                <Select value={placement} onValueChange={setPlacement}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  max={sub?.expires_at ? sub.expires_at.slice(0, 10) : undefined} />
              </div>
            </div>
            <div>
              <Label>Pages to show on</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {PAGES.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm capitalize">
                    <Checkbox checked={pages.includes(p)} onCheckedChange={() => togglePage(p)} /> {p}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="secondary" onClick={() => setPreviewOpen(true)} disabled={!imageUrl || !title.trim()}>
              <Sparkles className="h-4 w-4 mr-2" /> Preview placements
            </Button>
            <Button onClick={submit} disabled={submitting || !imageUrl || !title.trim()}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BannerPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={title}
        description={description}
        imageUrl={imageUrl}
        targetUrl={targetUrl}
        placement={placement}
        pages={pages}
        onConfirm={() => { setPreviewOpen(false); submit(); }}
        confirmLabel="Looks good — submit"
      />
    </div>
  );
}
